// useCountDown customHook

import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

const useCountDown = (timeExpiration: string | undefined) => {
  const [over, setOver] = useState(false);
  const [timer, setTimer] = useState({ hours: 0, minutes: 0, seconds: 10 });
  const { hours, minutes, seconds } = timer ?? {};
  const timerRef = useRef<ReturnType<typeof setInterval> | null>();

  const renderTimeNode = (value: number) => {
    return value.toString().padStart(1, "0");
  };

  const renderCompleteTime = useCallback(() => {
    if (
      typeof hours !== "number" ||
      typeof minutes !== "number" ||
      typeof seconds !== "number"
    ) {
      return;
    }
    if (hours === 0 && minutes === 0 && seconds === 0) return true;
    return `${renderTimeNode(hours)}:${
      minutes < 10 ? `0${renderTimeNode(minutes)}` : renderTimeNode(minutes)
    }:${
      seconds < 10 ? `0${renderTimeNode(seconds)}` : renderTimeNode(seconds)
    }`;
  }, [hours, minutes, seconds]);

  const renderTime = useCallback(() => {
    if (
      typeof hours !== "number" ||
      typeof minutes !== "number" ||
      typeof seconds !== "number"
    )
      return;
    if (over) return;
    let timer;

    if (hours <= 0 && minutes <= 0 && seconds <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      setOver(true);
      return;
    } else if (seconds === 0) {
      timer = {
        hours,
        minutes: minutes - 1,
        seconds: 59
      };
    } else {
      timer = {
        hours,
        minutes,
        seconds: seconds - 1
      };
    }
    setTimer(timer);
  }, [hours, minutes, seconds, over]);

  useEffect(() => {
    timerRef.current = setInterval(renderTime, 1000);
    return () => {
      if (!timerRef.current) return;
      clearInterval(timerRef.current);
    };
  }, [renderTime]);

  useEffect(() => {
    if (!timeExpiration) {
      if (timerRef.current) clearInterval(timerRef.current);
      setOver(true);
      return;
    }
    setOver(false);
    const now = dayjs();
    const isAfter = dayjs(timeExpiration).isAfter(now);
    if (!isAfter) return;
    const end = dayjs(timeExpiration);
    const hours = end.diff(now, "hours");
    const minutes = Math.floor(end.diff(now, "minutes") % 60);
    const seconds = Math.floor(
      end.diff(now.clone().add(minutes, "minutes"), "seconds") % 60
    );
    setTimer({ hours, minutes, seconds });
  }, [timeExpiration]);

  return renderCompleteTime();
};

export default useCountDown;
